import { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, ChevronUp } from "lucide-react";

function Header() {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [timeoutId, setTimeoutId] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileDropdowns, setMobileDropdowns] = useState({});
  const location = useLocation();

  useEffect(() => {
    setIsMobileMenuOpen(false);
    document.body.classList.remove("overflow-hidden");
  }, [location.pathname]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }
  }, [isMobileMenuOpen]);

  const handleMouseEnter = (menu) => {
    if (timeoutId) clearTimeout(timeoutId);
    setOpenDropdown(menu);
  };

  const handleMouseLeave = () => {
    const id = setTimeout(() => setOpenDropdown(null), 300);
    setTimeoutId(id);
  };

  const toggleMobileDropdown = (menu) => {
    setMobileDropdowns((prev) => ({
      ...prev,
      [menu]: !prev[menu],
    }));
  };

  const navItems = [
    {
      name: "ABOUT US",
      path: "/about",
      menu: "about",
      links: [
        { label: "ABOUT US", path: "/about" },
        { label: "OUR PRODUCTION PROCESS", path: "/productionprocess" },
      ],
    },
    {
      name: "MICROALGAE",
      path: "/microalgae",
      menu: "microalgae",
      links: [{ label: "MICROALGAE STRAINS", path: "/microalgae" }],
    },
    {
      name: "OUR PRODUCTS",
      path: "/products",
      menu: "OurProducts",
      links: [
        { label: "NUTRACEUTICAL & FOOD SUPPLEMENTS", path: "/products" },
        { label: "AGRO APPLICATION", path: "/products/agriculture" },
        { label: "ANIMAL & AQUA FEED", path: "/products/animal" },
      ],
    },
    {
      name: "R&D",
      path: "/research",
      menu: "r&d",
      links: [
        { label: "RESEARCH AND INNOVATION (R&I)", path: "/research" },
        { label: "OUR FUTURE PLAN", path: "/future" },
      ],
    },
  ];

  return (
    <header className="bg-gradient-to-r from-lime-300 via-lime-600 to-green-900 text-white shadow-md z-50 relative">
      <div className="container mx-auto flex items-center justify-between px-6 lg:px-10 py-4">
        {/* Logo */}
        <Link to="/" aria-label="Proalgenix Home" className="flex items-center">
          <img
            src="/Proalgenix_Logo.png"
            alt="Proalgenix Logo"
            className="h-12 sm:h-14 lg:h-20 w-auto hover:scale-110 transition-transform duration-300 dark:invert dark:brightness-[1.8]"
            loading="lazy"
            decoding="async"
            fetchPriority="high"
          />
        </Link>

        {/* Mobile Menu Button */}
        <button
          className="lg:hidden"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
        </button>

        {/* Desktop Nav */}
        <nav
          className="hidden lg:flex items-center space-x-8 text-base font-semibold"
          role="navigation"
          aria-label="Main navigation"
        >
          <NavLink to="/" className={({ isActive }) => isActive ? "text-yellow-400" : "hover:text-yellow-400 transition-colors"}>
            HOME
          </NavLink>

          {navItems.map(({ name, path, menu, links }) => (
            <div
              key={menu}
              className="relative"
              onMouseEnter={() => handleMouseEnter(menu)}
              onMouseLeave={handleMouseLeave}
            >
              <NavLink
                to={path}
                aria-haspopup="true"
                aria-expanded={openDropdown === menu}
                className={({ isActive }) =>
                  isActive ? "text-yellow-400" : "hover:text-yellow-400 transition-colors"
                }
              >
                {name}
              </NavLink>
              {openDropdown === menu && (
                <ul
                  className="absolute left-0 mt-2 w-56 bg-white text-black border border-gray-300 shadow-lg rounded-lg py-2 z-50"
                  role="menu"
                >
                  {links.map(({ label, path }) => (
                    <li key={label} role="menuitem">
                      <Link to={path} className="block px-5 py-2 hover:bg-gray-100 rounded transition">
                        {label}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}

          <NavLink to="/news-events" className={({ isActive }) => isActive ? "text-yellow-400" : "hover:text-yellow-400 transition-colors"}>
            NEWS & EVENTS
          </NavLink>
          <NavLink to="/contact" className={({ isActive }) => isActive ? "text-yellow-400" : "hover:text-yellow-400 transition-colors"}>
            CONTACT US
          </NavLink>
        </nav>
      </div>

      {/* Mobile Nav */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white text-black px-6 py-4 space-y-4 shadow-md">
          <NavLink to="/" onClick={() => setIsMobileMenuOpen(false)} className="block font-semibold hover:text-green-700">
            HOME
          </NavLink>

          {navItems.map(({ name, menu, links }) => (
            <div key={menu}>
              <button
                onClick={() => toggleMobileDropdown(menu)}
                className="w-full flex justify-between items-center font-semibold"
                aria-haspopup="true"
                aria-expanded={mobileDropdowns[menu] || false}
                aria-label={`Toggle ${name} submenu`}
              >
                {name}
                {mobileDropdowns[menu] ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </button>
              {mobileDropdowns[menu] && (
                <ul className="pl-4 pt-2 space-y-1" role="menu">
                  {links.map(({ label, path }) => (
                    <li key={label}>
                      <Link
                        to={path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="block text-sm hover:text-green-700"
                      >
                        {label}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}

          <NavLink to="/news-events" onClick={() => setIsMobileMenuOpen(false)} className="block font-semibold hover:text-green-700">
            NEWS & EVENTS
          </NavLink>
          <NavLink to="/contact" onClick={() => setIsMobileMenuOpen(false)} className="block font-semibold hover:text-green-700">
            CONTACT US
          </NavLink>
        </div>
      )}
    </header>
  );
}

export default Header;
